/**
 * One wind field agreed across several models.
 *
 * Edd asked for "an averaged wind, but without any extreme outliers", and that is
 * exactly what this is: a TRIMMED MEAN. The median finds the middle, anything
 * sitting far enough from it is set aside, and the mean of what is left is what
 * gets drawn. A plain mean would let one model that has invented a 45-knot squall
 * drag the whole field up; a bare median throws away the information in the
 * models that agree. Trimming keeps both halves of that.
 *
 * It is not a better forecast than the best model, and nothing here can tell you
 * which model that is on the day. What it is, is the answer that does not depend
 * on having picked the right one, which for a passage decision is usually the more
 * useful of the two.
 *
 * Directions are combined circularly throughout. The mean of 350 and 010 is 000,
 * not 180, and an average wind field that reports a southerly when every model
 * says north would be worse than no field at all.
 *
 * WHERE THE MODELS DISAGREE BADLY, IT SAYS SO rather than smoothing it away: the
 * spread comes back on every sample so the drawing can mark the points the models
 * are arguing over. An averaged number with no sense of the argument behind it is
 * the thing that makes a consensus forecast dangerous.
 */
import type { WindSample } from './index';
import { fetchRealWindField, windGrid, type RealWindField, type RealModelId } from './realWeather';
import { agreeScalar, agreeDirection, circularMean, AGREEMENT_THRESHOLDS } from './consensus';

/**
 * The models blended for the consensus field.
 *
 * The four that run far enough ahead to still be answering at the end of a
 * passage, and that are independent enough of each other to be worth averaging:
 * two European, one American, one from the Met Office. Short-range models are
 * left out on purpose, because a model that stops after two days would silently
 * drop out of the blend partway along the forecast and change the answer without
 * saying so.
 */
export const CONSENSUS_MODEL_IDS: RealModelId[] = [
  'ukmo_seamless',
  'ecmwf_ifs',
  'icon_seamless',
  'gfs_seamless',
];

/** A consensus sample: the agreed wind, plus how much the models argued about it. */
export interface ConsensusWindSample extends WindSample {
  /** How many models contributed after trimming. */
  count: number;
  /** Highest minus lowest speed across the models that answered, knots. */
  spreadKn: number;
  /** Angular spread of the directions, degrees. */
  spreadDeg: number;
}

/** The mean of the values not flagged as outliers, or null if there are none. */
function trimmedMean(values: { modelId: string; value: number | null }[]): number | null {
  const c = agreeScalar(values, AGREEMENT_THRESHOLDS.windKn);
  if (c.value == null) return null;
  const kept = values.filter(
    (v) => v.value != null && Number.isFinite(v.value) && !c.outliers.includes(v.modelId),
  );
  // Everything was an outlier, which can happen with two models far apart. The
  // median is still the honest middle, so fall back to it rather than to nothing.
  if (!kept.length) return c.value;
  return kept.reduce((a, v) => a + (v.value as number), 0) / kept.length;
}

/** The same, circularly, for a direction. */
function trimmedMeanDeg(values: { modelId: string; value: number | null }[]): number | null {
  const c = agreeDirection(values, AGREEMENT_THRESHOLDS.dirDeg);
  if (c.value == null) return null;
  const kept = values.filter(
    (v) => v.value != null && Number.isFinite(v.value) && !c.outliers.includes(v.modelId),
  );
  if (!kept.length) return c.value;
  return circularMean(kept.map((v) => v.value as number));
}

/**
 * Combine several already-fetched fields into one, at one instant.
 *
 * The fields share a grid, because they were all built from the same bounds by
 * windGrid, so points line up by index. Hours are matched by TIMESTAMP, since the
 * models publish different run lengths and lining up index against index would
 * compare different hours.
 */
export function combineFields(
  fields: { modelId: string; field: RealWindField }[],
  targetMs: number,
): ConsensusWindSample[] {
  const usable = fields.filter((f) => f.field.points.length && f.field.timesMs.length);
  if (!usable.length) return [];

  const points = usable[0].field.points;
  const out: ConsensusWindSample[] = [];

  for (let p = 0; p < points.length; p++) {
    const speeds: { modelId: string; value: number | null }[] = [];
    const dirs: { modelId: string; value: number | null }[] = [];
    for (const { modelId, field } of usable) {
      // Nearest hour in THIS model's own time grid.
      let t = -1;
      let best = Infinity;
      for (let i = 0; i < field.timesMs.length; i++) {
        const d = Math.abs(field.timesMs[i] - targetMs);
        if (d < best) {
          best = d;
          t = i;
        }
      }
      const sp = t < 0 ? NaN : Number(field.speedKn[p]?.[t]);
      const dr = t < 0 ? NaN : Number(field.dirFromDeg[p]?.[t]);
      speeds.push({ modelId, value: Number.isFinite(sp) ? sp : null });
      dirs.push({ modelId, value: Number.isFinite(dr) ? dr : null });
    }

    const speedKn = trimmedMean(speeds);
    const dirFromDeg = trimmedMeanDeg(dirs);
    if (speedKn == null || dirFromDeg == null) continue;

    const sc = agreeScalar(speeds, AGREEMENT_THRESHOLDS.windKn);
    const dc = agreeDirection(dirs, AGREEMENT_THRESHOLDS.dirDeg);
    out.push({
      position: points[p],
      speedKn,
      dirFromDeg,
      count: sc.count,
      spreadKn: sc.spread,
      spreadDeg: dc.spread,
    });
  }
  return out;
}

/**
 * Fetch every consensus model over the bounds and blend them.
 *
 * Each model is fetched independently and a failure is dropped, so one model
 * being late or down thins the blend rather than emptying the chart. Throws only
 * when none of them answered, which is a real "no forecast here" and should be
 * shown as one.
 */
export async function fetchConsensusField(
  bounds: [number, number, number, number],
  targetMs: number,
  modelIds: RealModelId[] = CONSENSUS_MODEL_IDS,
): Promise<{ samples: ConsensusWindSample[]; used: string[]; failed: string[] }> {
  const results = await Promise.all(
    modelIds.map(async (id) => {
      try {
        return { modelId: id, field: await fetchRealWindField(bounds, id) };
      } catch {
        return { modelId: id, field: null };
      }
    }),
  );
  const ok = results.filter((r) => r.field) as { modelId: string; field: RealWindField }[];
  const failed = results.filter((r) => !r.field).map((r) => r.modelId);
  if (!ok.length) throw new Error('No model answered for this area');
  return { samples: combineFields(ok, targetMs), used: ok.map((r) => r.modelId), failed };
}

/** The grid the consensus is built on, for anything that needs it up front. */
export { windGrid };
